import React, { FC, useState } from "react";
import { TreatmentsContainerLocation } from "./TreatmentsContainerLocation.csharp";
import { TreatmentsLocationsFilterItem } from "./TreatmentsLocationsFilterItem";
import ClickAwayListener from "react-click-away-listener";
import { dontFireOnTouchEventOnTargetHandler } from "../Helpers/clickAwayListenerHelpers";

interface TreatmentLocationFilterProps {
  availableLocations: TreatmentsContainerLocation[];
  selectedLocations: TreatmentsContainerLocation[];
  onChange: (locations: TreatmentsContainerLocation[]) => void;
  selectAllLabel: string;
  selectLocationsLabel: string;
}

export const TreatmentLocationFilter: FC<TreatmentLocationFilterProps> = ({
  availableLocations,
  selectedLocations,
  onChange,
  selectAllLabel,
  selectLocationsLabel,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const toggleDropdown = () => setShowDropdown(!showDropdown);
  const onClickAway = (e: Event) => {
    dontFireOnTouchEventOnTargetHandler(
      "button-location-filter",
      () => showDropdown && setShowDropdown(false),
      e
    );
  };

  const selectAllLocations = () => {
    if (
      availableLocations.flatMap((l) => [l, ...l.centers]).length ==
      selectedLocations.length
    ) {
      onChange([]);
    } else {
      const locations = availableLocations.flatMap((l) => [l, ...l.centers]);
      onChange(locations);
    }
  };

  const locationOnClick = (location: TreatmentsContainerLocation) => {
    if (selectedLocations.includes(location)) {
      const locationWithCenters = [location, ...location.centers];
      const parent = availableLocations.find(
        (l) => l.locationId == location.locationId
      );
      let newLocations = selectedLocations.filter(
        (l) => locationWithCenters.includes(l) == false
      );
      const allCentersSelected = parent!.centers.every((c) =>
        newLocations.includes(c)
      );
      if (allCentersSelected === false) {
        newLocations = newLocations.filter((l) => l !== parent);
      }
      onChange(
        newLocations.filter((val, index, self) => self.indexOf(val) == index)
      );
    } else {
      const parent = availableLocations.find(
        (l) => l.locationId == location.locationId
      );
      const newLocations = [
        ...selectedLocations,
        location,
        ...location.centers,
      ];

      const allCentersSelected = parent!.centers.every((c) =>
        newLocations.includes(c)
      );

      if (allCentersSelected) {
        newLocations.push(parent!);
      }
      onChange(
        newLocations.filter((val, index, self) => self.indexOf(val) == index)
      );
    }
  };

  const getInputValue = () => {
    const allSelected = availableLocations.every(
      (l) =>
        selectedLocations.includes(l) &&
        l.centers.every((c) => selectedLocations.includes(c))
    );

    if (allSelected) {
      return "Alle";
    } else if (selectedLocations.length > 0) {
      let names = [];
      for (const loc of selectedLocations) {
        if (
          loc.id == loc.locationId &&
          loc.centers.every((c) => selectedLocations.includes(c))
        ) {
          names.push(loc.name);
        } else if (
          selectedLocations.some((l) => l.id == loc.locationId) == false
        ) {
          names.push(loc.name);
        }
      }
      return names.join(", ");
    } else {
      return "Du må velge minst et sted";
    }
  };

  const allLocationsChecked = () =>
    availableLocations.flatMap((l) => [l, ...l.centers]).length ==
    selectedLocations.length;

  return (
    <>
      <span className="label d-block" id="dropdownLabel">
        {selectLocationsLabel}
      </span>
      <button
        className="button-location-filter form-control form-select max-width text-truncate m-0"
        onClick={toggleDropdown}
        aria-haspopup="true"
        aria-expanded={showDropdown}
        aria-controls="multiselect-popup-container"
        aria-labelledby="dropdownLabel"
      >
        {getInputValue()}
      </button>

      <ClickAwayListener onClickAway={onClickAway}>
        <div id="multiselect-popup-container" aria-live="polite">
          {showDropdown && (
            <div className="multiselect-popup p-2 pb-0">
              <div className="pb-1 mb-2 border-bottom border-info">
                <div className="form-check">
                  <label className="form-check-label fw-bolder">
                    <input
                      type="checkbox"
                      onChange={selectAllLocations}
                      className="form-check-input form-check-input--light"
                      checked={allLocationsChecked()}
                    />
                    {selectAllLabel}
                  </label>
                </div>
              </div>
              <ul className="no-bullets m-0 p-0 list-columns two-col-lg">
                {availableLocations.map((location) => (
                  <li className="pb-1 pb-md-2" key={location.id}>
                    <TreatmentsLocationsFilterItem
                      isChecked={selectedLocations.includes(location)}
                      bolderLabel
                      onChange={() => locationOnClick(location)}
                      label={location.name}
                    />
                    <ul className="no-bullets p-0">
                      {location.centers.map((center) => (
                        <li key={center.id}>
                          <TreatmentsLocationsFilterItem
                            isChecked={selectedLocations.includes(center)}
                            onChange={() => locationOnClick(center)}
                            label={center.name}
                          />
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </ClickAwayListener>
    </>
  );
};
